/**
 * Shared Moyasar payment config – build test or live config for donation checkout.
 * Used by UnifiedDonationModal and CartPageContent.
 * Client-side tokenization for Custom UI: card data stays in browser, only token is sent to backend.
 */
import { paymentService } from "@/lib/api/services";

const MOYASAR_TOKENS_URL = "https://api.moyasar.com/v1/tokens";
const MOYASAR_PAYMENTS_URL = "https://api.moyasar.com/v1/payments";

export interface MoyasarInitResponse {
  publishableKey: string;
  amount: number;
  currency: string;
  description: string;
  callbackUrl: string;
  metadata: Record<string, string>;
}

/**
 * Builds Moyasar init config from backend association configuration.
 * This prevents publishable/secret key mismatches across environments.
 */
export async function buildMoyasarConfig(params: {
  opCode: string;
  payAmount: number;
  associationId: number | string;
  mobile: string;
}): Promise<MoyasarInitResponse> {
  const { opCode, associationId: assocId } = params;
  const initiateResponse = await paymentService.moyasar.initiate({
    operationCode: opCode,
    associationId: Number(assocId) || 0,
  });
  if (!initiateResponse.success || !initiateResponse.data) {
    throw new Error(
      initiateResponse.message || "Failed to initiate payment",
    );
  }
  return initiateResponse.data as MoyasarInitResponse;
}

/** Card data for tokenization – must be collected in browser, never sent to our backend. */
export interface MoyasarTokenizeCardParams {
  name: string;
  number: string;
  month: string;
  year: string;
  cvc: string;
}

export interface MoyasarTokenResponse {
  id: string;
  status?: string;
  message?: string;
}

export interface MoyasarSavedCardPaymentResponse {
  paymentId: string;
  status?: string;
  transactionUrl: string;
  /** Present only once the payment is `paid`; absent before the 3DS redirect. */
  token?: string;
  payment: unknown;
}

/** Card fields Moyasar may reject during tokenization. */
export type MoyasarCardField = "name" | "number" | "month" | "year" | "cvc";

export type MoyasarCardFieldErrors = Partial<
  Record<MoyasarCardField, string>
>;

/** Tokenization failure with optional per-field errors from Moyasar. */
export class MoyasarTokenizeError extends Error {
  readonly fieldErrors: MoyasarCardFieldErrors;

  constructor(message: string, fieldErrors: MoyasarCardFieldErrors = {}) {
    super(message);
    this.name = "MoyasarTokenizeError";
    this.fieldErrors = fieldErrors;
  }
}

const MOYASAR_CARD_FIELDS = new Set<MoyasarCardField>([
  "name",
  "number",
  "month",
  "year",
  "cvc",
]);

function parseMoyasarFieldErrors(
  errors: unknown,
): MoyasarCardFieldErrors {
  if (!errors || typeof errors !== "object") return {};
  const fieldErrors: MoyasarCardFieldErrors = {};
  for (const [rawKey, value] of Object.entries(
    errors as Record<string, unknown>,
  )) {
    const key = rawKey.replace(/^source\./, "") as MoyasarCardField;
    if (!MOYASAR_CARD_FIELDS.has(key)) continue;
    const text = Array.isArray(value)
      ? value.filter((v) => typeof v === "string").join(", ")
      : typeof value === "string"
        ? value
        : "";
    if (text) fieldErrors[key] = text;
  }
  return fieldErrors;
}

/**
 * Tokenize card at Moyasar from the client. Uses publishable key only.
 * Call this from the browser; do not send card data to our server.
 */
export async function tokenizeCardToMoyasar(
  publishableKey: string,
  card: MoyasarTokenizeCardParams,
): Promise<string> {
  const number = card.number.replace(/\s/g, "");
  if (!number || !card.month || !card.year || !card.cvc) {
    throw new Error("Card number, expiry, and CVC are required");
  }
  const body = {
    publishable_api_key: publishableKey,
    name: card.name || "Cardholder",
    number,
    month: card.month.padStart(2, "0"),
    year: card.year.length === 4 ? card.year.slice(-2) : card.year,
    cvc: card.cvc,
    save_only: true,
  };
  const resp = await fetch(MOYASAR_TOKENS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await resp.json().catch(() => null)) as
    | { id?: string; message?: string; errors?: unknown }
    | null;
  if (!resp.ok) {
    const fieldErrors = parseMoyasarFieldErrors(data?.errors);
    const msg =
      (data && typeof data === "object" && data.message) ||
      `Tokenization failed (${resp.status})`;
    throw new MoyasarTokenizeError(msg, fieldErrors);
  }
  if (!data || typeof data.id !== "string") {
    throw new Error("Invalid token response");
  }
  return data.id;
}

/**
 * First recurring deduction payment: Moyasar must create the payment and save
 * the card in one step so the returned source.token becomes active after 3DS.
 */
export async function createSavedCardMoyasarPayment(params: {
  publishableKey: string;
  card: MoyasarTokenizeCardParams;
  amount: number;
  currency: string;
  description: string;
  callbackUrl: string;
  metadata: Record<string, string>;
}): Promise<MoyasarSavedCardPaymentResponse> {
  const number = params.card.number.replace(/\s/g, "");
  if (!number || !params.card.month || !params.card.year || !params.card.cvc) {
    throw new Error("Card number, expiry, and CVC are required");
  }

  const resp = await fetch(MOYASAR_PAYMENTS_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Basic ${btoa(`${params.publishableKey}:`)}`,
    },
    body: JSON.stringify({
      amount: params.amount,
      currency: params.currency || "SAR",
      description: params.description || "Donation",
      callback_url: params.callbackUrl,
      metadata: params.metadata,
      source: {
        type: "creditcard",
        name: params.card.name || "Cardholder",
        number,
        month: params.card.month.padStart(2, "0"),
        year:
          params.card.year.length === 4
            ? params.card.year.slice(-2)
            : params.card.year,
        cvc: params.card.cvc,
        save_card: true,
      },
    }),
  });
  const data = (await resp.json().catch(() => null)) as
    | {
        id?: string;
        status?: string;
        message?: string;
        errors?: unknown;
        source?: {
          token?: string;
          transaction_url?: string;
        };
      }
    | null;

  if (!resp.ok) {
    const fieldErrors = parseMoyasarFieldErrors(data?.errors);
    const msg =
      (data && typeof data === "object" && data.message) ||
      `Payment creation failed (${resp.status})`;
    throw new MoyasarTokenizeError(msg, fieldErrors);
  }

  // The card token only becomes usable once the payment reaches `paid`, so it
  // is normally absent here (payment is still `initiated`). Requiring it would
  // block the donor before 3DS; verify/webhook persist it after the redirect.
  if (!data?.id || !data.source?.transaction_url) {
    throw new Error("Invalid saved-card payment response");
  }

  return {
    paymentId: data.id,
    status: data.status,
    token: data.source.token,
    transactionUrl: data.source.transaction_url,
    payment: data,
  };
}
